import { h, Component } from 'preact';
import { sortObjectByOrder, joinClasses } from 'utils/utils';
import { navigateInternally, getValueForClientSize, onMarkdownClick, getRelativePageUrl } from 'utils/bolt';
import { useDynamicAssetFolder } from 'utils/assets';

import Section from 'components/bolt/section/section';
import HTML from 'components/core/html/html';

import s from 'components/bolt/imageTiles/imageTiles.sss';

const isExternalLink = str => /^((https?:)|mailto:)/.test(str);
const getHref = (link) => (isExternalLink(link) ? link : getRelativePageUrl(link) || '#');

// TODO: export for overrides ? This might not need to be a class though
class Link extends Component {
	constructor(props) {
		super(props);
		this.onClick = this.onClick.bind(this);
	}

	onClick(e) {
		const destination = this.props.destination;
		// This check should not be necessary (navigateInternally should not do anything for external links), but avoids some unnecessary processing
		if (!isExternalLink(destination)) {
			navigateInternally(destination);
		}
		this.props.onClick?.(e, this.props);
	}

	render({ destination, sameWindow, children, ...props }) {
		if (!destination) return children[0];
		const target = isExternalLink(destination) && !sameWindow ? '_blank' : null;
		return (
			<a {...props} href={getHref(destination)} target={target} onClick={this.onClick}>
				{children}
			</a>
		);
	}
}

export default class ImageTiles extends Section {
	constructor(props) {
		super(props);
		const pageData = props?.appData?.pages[props.id].data || {};
		this.state = {
			...this.state,
			title: pageData.titleCopy,
			description: pageData.descriptionCopy,
			tiles: sortObjectByOrder(pageData.tiles, (value, key) => ({ key, ...value })),
		};

		this.onClickTile = this.onClickTile.bind(this);
		this.onClickAnnotation = this.onClickAnnotation.bind(this);
		this.renderTile = this.renderTile.bind(this);
	}

	onClickTile(e, props) {
		window.firePowsterCustomEvent?.('imagetile-link-click', 'imagetiles', [e.target?.dataset?.name, props.destination].filter(Boolean).join(' - '));
	}

	onClickAnnotation(e, props) {
		window.firePowsterCustomEvent?.('imagetile-annotation-click', 'imagetiles', [e.target?.dataset?.name, props.destination].filter(Boolean).join(' - '));
	}

	renderAnnotation(tile) {
		if (!tile.annotation) return;
		const content = <HTML markdown content={tile.annotation} />;
		if (!tile.annotationLink) {
			return <div class={s.annotation}>{content}</div>;
		}
		return (
			<Link class={s.annotation} destination={tile.annotationLink} onClick={this.onClickAnnotation} data-name={tile.name || tile.annotation}>
				{content}
			</Link>
		);
	}

	getSrc(tile) {
		return useDynamicAssetFolder(tile.image || getValueForClientSize({
			desktop: tile.desktopSrc,
			mobile: tile.mobileSrc,
			tablet: tile.tabletSrc,
		}));
	}

	getVideoSrc(tile) {
		return useDynamicAssetFolder(tile.video || getValueForClientSize({
			desktop: tile.videoDesktopSrc,
			mobile: tile.videoMobileSrc,
			tablet: tile.videoTabletSrc,
		}));
	}

	renderTile(tile, index) {
		const imageSrc = this.getSrc(tile);
		const videoSrc = this.getVideoSrc(tile);
		const position = tile.annotationPosition || 'bottom';
		const tileClass = joinClasses(
			s.tile,
			position === 'top' && s['tile--annotation-top'],
			position === 'middle' && s['tile--annotation-middle']
		);
		let content = null;
		if (videoSrc) {
			content = <video class={s.video} alt={tile.alt} autoPlay loop muted src={videoSrc} />;
		} else if (imageSrc) {
			content = <img class={s.image} alt={tile.alt} src={imageSrc} />;
		}

		return (
			<div class={tileClass} key={tile.key || index}>
				{(position === 'top' || position === 'middle') && this.renderAnnotation(tile)}
				<Link destination={tile.link} sameWindow={tile.sameWindow} onClick={this.onClickTile}>{content}</Link>
				{position === 'bottom' && this.renderAnnotation(tile)}
			</div>
		);
	}

	renderTiles() {
		return (
			<div class={s.tiles} key="tiles">
				{this.state.tiles.map(this.renderTile)}
			</div>
		);
	}

	render(props, state) {
		const { title, description } = state;
		return (
			<div class={s.container} data-section={this.props.id}>
				{this.renderBgContent()}
				{!!title && <HTML tag="h3" class={joinClasses(s.heading, 'largeB')} content={title} markdown inline onClickCapture={onMarkdownClick} key="title" />}
				{!!description && <HTML tag="h4" class={joinClasses(s.description, 'xlarge')} content={description} markdown inline onClickCapture={onMarkdownClick} key="description" />}
				{this.renderTiles()}
			</div>
		);
	}
}
